import type { ReactNode } from 'react';
import { Button } from '@staff-app/ui';
import { CheckCircle2, Clock, ExternalLink, RefreshCw, Send } from 'lucide-react';
import { formatMinor, type IntlAllRequest } from '@staff-app/shared-types';
import { IntlInvoiceStatusBadge } from '@/features/intl-requests/components/IntlInvoiceStatusBadge';

function fmt(d?: string | null): string {
  if (!d) return '—';
  const parsed = new Date(d);
  return isNaN(+parsed) ? '—' : parsed.toLocaleString();
}

function Field({ label, value, mono }: { label: string; value?: ReactNode; mono?: boolean }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={`mt-0.5 break-words text-sm ${mono ? 'font-mono' : ''} ${empty ? 'text-muted-foreground' : ''}`}>
        {empty ? '—' : value}
      </dd>
    </div>
  );
}

/**
 * The NEXT STEP for the business user, by where the invoice stands (Paul, 2026-10-28): a draft has
 * not reached the customer, and the way to send it is the invoice's page in Stripe. Said in words
 * with the button beside it, rather than leaving "draft" in a status column to be interpreted.
 */
function NextStep({ request }: { request: IntlAllRequest }) {
  const status = request.invoiceStatus ?? 'draft';
  const open = request.invoiceUrl ? (
    <Button size="sm" asChild className="gap-1.5">
      <a href={request.invoiceUrl} target="_blank" rel="noreferrer" data-testid="air-invoice-send-link">
        <Send className="h-3.5 w-3.5" />
        {'Open in Stripe to send'}
        <ExternalLink className="h-3 w-3" />
      </a>
    </Button>
  ) : null;

  if (status === 'draft') {
    return (
      <div className="space-y-2 rounded-md border border-warning/40 bg-warning/10 p-3" data-testid="air-invoice-next-send">
        <p className="text-sm font-medium">{'Next: send the invoice to the customer'}</p>
        <ol className="list-decimal space-y-0.5 pl-5 text-xs text-muted-foreground">
          <li>{'Open the invoice in Stripe.'}</li>
          <li>{'Check the lines, the total and the customer’s email address.'}</li>
          <li>{'Click “Send invoice”. Stripe finalizes it and emails it to the customer.'}</li>
          <li>{'Come back and press Refresh from Stripe — the status changes to open.'}</li>
        </ol>
        {request.invoiceAutoSendAt ? (
          <p className="flex items-center gap-1.5 text-xs text-warning" data-testid="air-invoice-autosend">
            <Clock className="h-3.5 w-3.5 shrink-0" />
            {`If nobody sends it first, Stripe sends it automatically at ${fmt(request.invoiceAutoSendAt)}.`}
          </p>
        ) : (
          <p className="text-xs text-warning" data-testid="air-invoice-manual">
            {'It will not send itself — the customer receives nothing until someone sends it from Stripe.'}
          </p>
        )}
        {open}
      </div>
    );
  }
  if (status === 'open') {
    return (
      <p className="flex items-center gap-1.5 text-sm" data-testid="air-invoice-next-sent">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
        {'Sent to the customer — waiting for payment.'}
      </p>
    );
  }
  if (status === 'paid') {
    return (
      <p className="flex items-center gap-1.5 text-sm" data-testid="air-invoice-next-paid">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
        {`Paid${request.invoicePaidAt ? ` ${fmt(request.invoicePaidAt)}` : ''}.`}
      </p>
    );
  }
  return (
    <p className="text-sm text-destructive" data-testid="air-invoice-next-closed">
      {`The invoice is ${status} — nothing is owed on it and nothing may ship against it.`}
    </p>
  );
}

/**
 * The invoice, once raised — the same fields International Requests' Invoice section shows, plus
 * the next step to take in Stripe.
 */
export function InvoiceSection({
  request,
  onRefresh,
  refreshing,
}: {
  request: IntlAllRequest;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  return (
    <div className="space-y-3" data-testid="air-invoice-section">
      <NextStep request={request} />
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Invoice number" value={request.invoiceNumber} mono />
        <Field label="Status" value={request.invoiceStatus ? <IntlInvoiceStatusBadge status={request.invoiceStatus} /> : null} />
        <Field label="Stripe invoice id" value={request.stripeInvoiceId} mono />
        <Field
          label="Total"
          value={request.invoiceTotal != null ? formatMinor(request.invoiceTotal, request.currency) : null}
        />
        <Field label="Purchase order" value={request.poNumber} mono />
        <Field label="Raised by" value={request.invoiceRaisedBy} />
        <Field label="Raised at" value={fmt(request.invoiceRaisedAt)} />
        <Field label="Paid at" value={fmt(request.invoicePaidAt)} />
        <Field label="Last checked with Stripe" value={fmt(request.invoiceCheckedAt)} />
      </dl>
      {request.invoiceError ? <p className="text-sm text-destructive">{request.invoiceError}</p> : null}
      <div className="flex flex-wrap gap-2">
        {request.invoiceUrl ? (
          <Button size="sm" variant="outline" asChild className="gap-1">
            <a href={request.invoiceUrl} target="_blank" rel="noreferrer" data-testid="air-invoice-link">
              {'Open in Stripe'}
              <ExternalLink className="h-3 w-3" />
            </a>
          </Button>
        ) : null}
        <Button size="sm" variant="outline" onClick={onRefresh} disabled={refreshing} data-testid="air-invoice-refresh">
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Asking Stripe…' : 'Refresh from Stripe'}
        </Button>
      </div>
    </div>
  );
}
